import React, { useState } from "react";
import { apiResetPassword } from "../lib/api";
import { Sprout, Loader2, Lock, CheckCircle2 } from "lucide-react";

const formatErr = (d) => (typeof d === "string" ? d : d?.msg || "Something went wrong.");

export default function ResetPassword({ token }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const clearAndGoLogin = () => {
    window.history.replaceState(null, "", window.location.pathname);
    window.location.href = window.location.origin;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) return setErr("Password must be at least 6 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      await apiResetPassword(token, password);
      setDone(true);
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-forest-900 text-white flex flex-col items-center justify-center p-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="h-10 w-10 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-6 w-6" strokeWidth={2.4} /></div>
        <span className="font-heading font-extrabold text-2xl">Assort<span className="text-ai">IQ</span></span>
      </div>

      <div data-testid="reset-card" className="w-full max-w-sm rounded-xl bg-white text-forest-800 p-8 shadow-2xl">
        {done ? (
          <div className="text-center">
            <CheckCircle2 className="h-12 w-12 text-action-keep mx-auto" />
            <h1 className="mt-4 font-heading text-2xl font-extrabold">Password reset</h1>
            <p className="mt-2 text-sm text-forest-500">Your password has been updated. You can now sign in with your new password.</p>
            <button data-testid="reset-goto-login" onClick={clearAndGoLogin}
              className="mt-6 w-full rounded-md bg-forest-900 text-white py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-forest-800">
              Go to sign in
            </button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-extrabold">Choose a new password</h1>
            <p className="mt-1 text-sm text-forest-500">Enter a new password for your AssortIQ account.</p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="relative">
                <Lock className="h-4 w-4 text-forest-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="reset-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password" className="w-full rounded-md border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-800" />
              </div>
              <div className="relative">
                <Lock className="h-4 w-4 text-forest-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password" className="w-full rounded-md border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-800" />
              </div>
              {err && <p data-testid="reset-error" className="text-sm text-[#C0261F]">{err}</p>}
              <button data-testid="reset-submit-button" type="submit" disabled={busy}
                className="w-full flex items-center justify-center gap-2 rounded-md bg-ai text-white py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Reset password
              </button>
            </form>
            <button data-testid="reset-cancel" onClick={clearAndGoLogin} className="mt-4 w-full text-center text-sm text-forest-500 hover:underline">
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
